/**
 * Theme System for Performance Cockpit
 * 
 * This file defines multiple themes including the current "light" theme
 * and provides infrastructure for theme switching without breaking existing styles.
 */

// =============================================================================
// THEME TYPES
// =============================================================================

export interface ThemeColors {
  // Brand colors
  cockpitBg: string;
  cockpitBlue: {
    light: string;
    default: string;
    dark: string;
  };
  title: string;
  sidebarBg: string; // Dedicated sidebar background color
  quickRange: string;
  
  // Status colors
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
    disabled: string;
  };
  
  // Text colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    muted: string;
    white: string;
    inverse: string; // For dark themes
  };
  
  // Background colors
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
    overlay: string;
    transparent: string;
    surface: string; // Cards, modals
    elevated: string; // Elevated surfaces
  };
  
  // Border colors
  border: {
    primary: string;
    secondary: string;
    accent: string;
    transparent: string;
  };
  
  // Interactive states
  interactive: {
    hover: string;
    active: string;
    disabled: string;
    focus: string;
  };
}

export interface Theme {
  name: string;
  displayName: string;
  colors: ThemeColors;
  isDark: boolean;
  // Chart specific colors
  chart: {
    primary: string[];
    performance: string[];
    warning: string[];
    error: string[];
    neutral: string[];
    multi: string[];
  };
}

// =============================================================================
// LIGHT THEME (Current Default)
// =============================================================================

export const lightTheme: Theme = {
  name: 'light',
  displayName: 'Light Theme',
  isDark: false,
  colors: {
    // Brand colors (keeping current values)
    cockpitBg: '#f8fafc', // Current cockpitbg
    cockpitBlue: {
      light: '#84d1f1', // Current cockpitblue-light
      default: '#037ac7', // Current cockpitblue
      dark: '#025a94', // Current cockpitblue-dark
    },
    title: '#025a94', // Brand dark blue - used for sidebar background
    sidebarBg: '#025a94', // Dedicated sidebar background - always brand blue
    quickRange: '#f59e0b', // Current quickrange
    
    // Status colors
    status: {
      success: '#10b981', // green-500
      warning: '#f59e0b', // yellow-500
      error: '#ef4444', // red-500
      info: '#3b82f6', // blue-500
      disabled: '#9ca3af', // gray-400
    },
    
    // Text colors
    text: {
      primary: '#111827', // gray-900
      secondary: '#374151', // gray-700
      tertiary: '#6b7280', // gray-600
      muted: '#9ca3af', // gray-500
      white: '#ffffff',
      inverse: '#ffffff', // White text for dark backgrounds
    },
    
    // Background colors
    background: {
      primary: '#ffffff', // white
      secondary: '#f9fafb', // gray-50
      tertiary: '#f3f4f6', // gray-100
      overlay: 'rgba(255, 255, 255, 0.8)',
      transparent: 'transparent',
      surface: '#ffffff', // Cards, modals
      elevated: '#ffffff', // Elevated surfaces
    },
    
    // Border colors
    border: {
      primary: '#d1d5db', // gray-300 - more visible than gray-200
      secondary: '#9ca3af', // gray-400 - darker for secondary borders
      accent: '#bfdbfe', // blue-200
      transparent: 'transparent',
    },
    
    // Interactive states
    interactive: {
      hover: '#f3f4f6', // gray-100
      active: '#e5e7eb', // gray-200
      disabled: '#f9fafb', // gray-50
      focus: '#3b82f6', // blue-500
    },
  },
  
  // Chart colors (current Highcharts config)
  chart: {
    primary: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
    performance: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0'],
    warning: ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a'],
    error: ['#ef4444', '#f87171', '#fca5a5', '#fecaca'],
    neutral: ['#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb'],
    multi: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
  },
};

// =============================================================================
// DARK THEME
// =============================================================================

export const darkTheme: Theme = {
  name: 'dark',
  displayName: 'Dark Theme',
  isDark: true,
  colors: {
    // Brand colors (adapted for dark)
    cockpitBg: '#1e293b', // slate-800 - matches component backgrounds
    cockpitBlue: {
      light: '#60a5fa', // blue-400
      default: '#3b82f6', // blue-500
      dark: '#1d4ed8', // blue-700
    },
    title: '#f8fafc', // Light text for dark backgrounds - use slate-50 for good contrast
    sidebarBg: '#025a94', // Dedicated sidebar background - always brand blue
    quickRange: '#fbbf24', // yellow-400
    
    // Status colors (brighter for dark backgrounds)
    status: {
      success: '#34d399', // emerald-400
      warning: '#fbbf24', // yellow-400
      error: '#f87171', // red-400
      info: '#60a5fa', // blue-400
      disabled: '#64748b', // slate-500
    },
    
    // Text colors
    text: {
      primary: '#f8fafc', // slate-50
      secondary: '#e2e8f0', // slate-200
      tertiary: '#cbd5e1', // slate-300
      muted: '#94a3b8', // slate-400
      white: '#ffffff',
      inverse: '#0f172a', // Dark text for light backgrounds
    },
    
    // Background colors
    background: {
      primary: '#1e293b', // slate-800
      secondary: '#334155', // slate-700
      tertiary: '#475569', // slate-600
      overlay: 'rgba(15, 23, 42, 0.8)', // slate-900 with opacity
      transparent: 'transparent',
      surface: '#1e293b', // Cards, modals
      elevated: '#334155', // Elevated surfaces
    },
    
    // Border colors
    border: {
      primary: '#475569', // slate-600
      secondary: '#64748b', // slate-500
      accent: '#3b82f6', // blue-500
      transparent: 'transparent',
    },
    
    // Interactive states
    interactive: {
      hover: '#334155', // slate-700
      active: '#475569', // slate-600
      disabled: '#1e293b', // slate-800
      focus: '#60a5fa', // blue-400
    },
  },
  
  // Chart colors (adapted for dark backgrounds)
  chart: {
    primary: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
    performance: ['#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'],
    warning: ['#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'],
    error: ['#f87171', '#fca5a5', '#fecaca', '#fee2e2'],
    neutral: ['#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9'],
    multi: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
  },
};

// =============================================================================
// HIGH CONTRAST THEME
// =============================================================================

export const highContrastTheme: Theme = {
  name: 'high-contrast',
  displayName: 'High Contrast',
  isDark: false,
  colors: {
    // Brand colors (high contrast)
    cockpitBg: '#ffffff',
    cockpitBlue: {
      light: '#0000ff',
      default: '#000080',
      dark: '#000040',
    },
    title: '#000040', // Brand dark blue (high contrast) - sidebar maintains brand identity
    sidebarBg: '#000040', // Dedicated sidebar background - always brand blue (high contrast)
    quickRange: '#ff8800',
    
    // Status colors (maximum contrast)
    status: {
      success: '#008000', // Pure green
      warning: '#ff8800', // Pure orange
      error: '#ff0000', // Pure red
      info: '#0000ff', // Pure blue
      disabled: '#808080', // Gray
    },
    
    // Text colors
    text: {
      primary: '#000000',
      secondary: '#333333',
      tertiary: '#666666',
      muted: '#999999',
      white: '#ffffff',
      inverse: '#ffffff',
    },
    
    // Background colors
    background: {
      primary: '#ffffff',
      secondary: '#f0f0f0',
      tertiary: '#e0e0e0',
      overlay: 'rgba(255, 255, 255, 0.95)',
      transparent: 'transparent',
      surface: '#ffffff',
      elevated: '#ffffff',
    },
    
    // Border colors
    border: {
      primary: '#000000',
      secondary: '#666666',
      accent: '#0000ff',
      transparent: 'transparent',
    },
    
    // Interactive states
    interactive: {
      hover: '#e0e0e0',
      active: '#cccccc',
      disabled: '#f8f8f8',
      focus: '#0000ff',
    },
  },
  
  // Chart colors (high contrast)
  chart: {
    primary: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
    performance: ['#008000', '#32cd32', '#90ee90', '#98fb98'],
    warning: ['#ff8800', '#ffa500', '#ffb347', '#ffd700'],
    error: ['#ff0000', '#ff4500', '#ff6347', '#ff7f7f'],
    neutral: ['#000000', '#333333', '#666666', '#999999'],
    multi: ['#025387', '#1E96FF', '#84D1F1', '#20CB6E', '#197B1B', '#496860', '#FEB82C', '#F56424', '#E83F5B', '#002E52'],
  },
};

// =============================================================================
// THEME REGISTRY
// =============================================================================

export const themes = {
  light: lightTheme,
  dark: darkTheme,
  'high-contrast': highContrastTheme,
} as const;

export type ThemeName = keyof typeof themes;

// =============================================================================
// THEME UTILITIES
// =============================================================================

export const getTheme = (themeName: ThemeName): Theme => {
  return themes[themeName] || themes.light;
};

export const getAvailableThemes = (): { name: ThemeName; displayName: string; isDark: boolean }[] => {
  return Object.values(themes).map(theme => ({
    name: theme.name as ThemeName,
    displayName: theme.displayName,
    isDark: theme.isDark,
  }));
};

// =============================================================================
// THEME CSS VARIABLE GENERATION
// =============================================================================

export const generateCSSVariables = (theme: Theme): Record<string, string> => {
  const variables: Record<string, string> = {};
  
  // Brand colors
  variables['--cockpit-bg'] = theme.colors.cockpitBg;
  variables['--cockpit-blue-light'] = theme.colors.cockpitBlue.light;
  variables['--cockpit-blue'] = theme.colors.cockpitBlue.default;
  variables['--cockpit-blue-dark'] = theme.colors.cockpitBlue.dark;
  variables['--title'] = theme.colors.title;
  variables['--sidebar-bg'] = theme.colors.sidebarBg;
  variables['--quick-range'] = theme.colors.quickRange;
  
  // Status colors
  Object.entries(theme.colors.status).forEach(([key, value]) => {
    variables[`--status-${key}`] = value;
  });
  
  // Text colors
  Object.entries(theme.colors.text).forEach(([key, value]) => {
    variables[`--text-${key}`] = value;
  });
  
  // Text info color (computed based on theme)
  variables['--text-info'] = theme.isDark ? '#60a5fa' : '#3b82f6'; // blue-400 for dark, blue-500 for light
  
  // Background colors
  Object.entries(theme.colors.background).forEach(([key, value]) => {
    variables[`--bg-${key}`] = value;
  });
  
  // Border colors
  Object.entries(theme.colors.border).forEach(([key, value]) => {
    variables[`--border-${key}`] = value;
  });
  
  // Interactive states
  Object.entries(theme.colors.interactive).forEach(([key, value]) => {
    variables[`--interactive-${key}`] = value;
  });
  
  // Subtle background colors (computed based on theme)
  if (theme.isDark) {
    variables['--bg-success-subtle'] = 'rgba(52, 211, 153, 0.2)'; // emerald-400 with opacity
    variables['--bg-warning-subtle'] = 'rgba(251, 191, 36, 0.2)'; // yellow-400 with opacity
    variables['--bg-error-subtle'] = 'rgba(248, 113, 113, 0.2)'; // red-400 with opacity
    variables['--bg-info-subtle'] = 'rgba(96, 165, 250, 0.2)'; // blue-400 with opacity
  } else {
    variables['--bg-success-subtle'] = '#f0fdf4'; // green-50
    variables['--bg-warning-subtle'] = '#fefce8'; // yellow-50
    variables['--bg-error-subtle'] = '#fef2f2'; // red-50
    variables['--bg-info-subtle'] = '#eff6ff'; // blue-50
  }
  
  return variables;
};

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  themes,
  getTheme,
  getAvailableThemes,
  generateCSSVariables,
  lightTheme,
  darkTheme,
  highContrastTheme,
};
